// ═══════════════════════════════════════════════════════════════════════════
// DYN-5 follow-up — derive low-level DimensionalConstraint descriptors from
// BPT / constraint-based parameters.
//
// CONTEXT
// ───────
// AutoCAD dimensional constraint parameters (constraint-horizontal /
// constraint-vertical / constraint-aligned / constraint-angular /
// constraint-radial / constraint-diameter) carry their own reference points
// (pointA / pointB / vertex / center) but NOT explicit entity references.
// The constraint solver (`solveBlockConstraints`) requires descriptors with
// `entityIds[]` and `entitySubselectors[]` — so to drive the solver from a
// BPT-driven block we must resolve each constraint-parameter point back to
// the entity feature it sits on (line endpoint, circle center, polyline
// vertex, ...).
//
// ALGORITHM
// ─────────
// For every numeric constraint parameter whose runtime value is non-zero:
//   1. Resolve each referenced point (pointA/B/vertex/center) to the
//      (entityId, subselector) pair that sits at that coordinate within
//      epsilon tolerance. Subselectors match those accepted by
//      `constraintSolverIntegration.resolvePointRef`:
//        - Line           : "start" / "end"
//        - Circle / Arc   : "center" (default)
//        - LWPOLYLINE     : "v<n>"  (vertex index)
//        - Text / INSERT  : ""      (whole-entity anchor)
//   2. Emit a DimensionalConstraint with kind mapped from the parameter
//      kind, currentValue converted to the solver's canonical units (length
//      in drawing units, angle in RADIANS — parameters store degrees).
//
// If any required point cannot be resolved to an entity feature, the
// constraint is skipped and a warning is emitted. The caller aggregates
// warnings into `errors[]` so they surface through the evaluator's normal
// diagnostic channel.
//
// AUTOCAD PARITY CAVEAT
// ─────────────────────
// AutoCAD's real dimensional constraints carry persistent back-references
// (stored as DXF ACDBASSOC2DCONSTRAINTGROUP sub-entity chains) to the
// geometry they dimension. The drawing-model schema does not yet surface
// those links — the DYN-3 Batch A parser emits parameter points only. This
// collector infers the link geometrically, which matches the visual
// observation made in AutoCAD (the dimension endpoints sit on the feature
// vertices). A follow-up will switch to explicit ACDBASSOC2DCONSTRAINTGROUP
// wiring once that schema field lands, eliminating the coordinate-match
// step.
// ═══════════════════════════════════════════════════════════════════════════

import type {
  AssociatedEntityRef,
  DynamicParameter,
} from "@dxf-ai-editor/drawing-model";

import type { DimensionalConstraint } from "./constraintSolverIntegration";
import type { EntityLike } from "./geometry";
import {
  resolveSubselector,
  type SubselectorResolverEntity,
} from "./subselectorResolution";

/**
 * Coordinate-match tolerance. AutoCAD's own dimension endpoints are laid
 * down with full double precision; a user editing the block in the author
 * environment sees the dimension snap to the underlying vertex exactly.
 * Round-trip through DXF preserves that alignment to ~1e-9 — `1e-6` gives
 * us four orders of magnitude of slack for accumulated float error from
 * DYN-5 axial stretch / constraint stretch passes.
 */
const MATCH_EPS = 1e-6;

const DEG_TO_RAD = Math.PI / 180;

interface Vec2 {
  readonly x: number;
  readonly y: number;
}

interface EntityRef {
  readonly id: string;
  readonly subselector: string;
}

function distSq(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function near(a: Vec2, b: Vec2): boolean {
  return distSq(a, b) <= MATCH_EPS * MATCH_EPS;
}

/**
 * Convert an `AssociatedEntityRef` (DXF-sourced entity handle + persistent
 * subent ID pair) to the (entityId, subselector) pair consumed by the
 * solver integration layer.
 *
 * When the ref's target entity is present in `entityById`, the subselector
 * is resolved canonically via `resolveSubselector` (see that function's
 * docstring for the full AutoCAD-parity table: "start"/"end"/"v<i>"/
 * "edge<i>"/"center"). If the entity can't be looked up (rare — would
 * mean the ref points at a handle that isn't in the evaluator's scope)
 * we fall back to the legacy `v<subentIdA>` placeholder so downstream
 * consumers still get a deterministic hook.
 *
 * Returns null when `entityId` is empty (the geom-dep had no resolvable
 * entity handle). The caller should skip that reference and fall back
 * to the coordinate-match path.
 */
function assocRefToEntityRef(
  ref: AssociatedEntityRef,
  entityById: ReadonlyMap<string, SubselectorResolverEntity>,
): EntityRef | null {
  if (ref.entityId.length === 0) return null;
  const entity = entityById.get(ref.entityId);
  if (entity === undefined) {
    // Entity not in current scope — fall back to legacy placeholder.
    // Unknown subselector labels are treated as whole-entity anchors
    // by constraintSolverIntegration, which keeps the solver happy.
    return {
      id: ref.entityId,
      subselector: `v${ref.subentIdA}`,
    };
  }
  const subselector = resolveSubselector(
    entity,
    ref.subentType,
    ref.subentIdA,
    ref.subentIdB,
    ref.isSingleEdge ?? false,
  );
  return {
    id: ref.entityId,
    subselector,
  };
}

/**
 * Try to resolve a constraint endpoint to an entity ref using the
 * pre-parsed `associatedEntities[]` list. Returns the `[index]`-th
 * resolvable ref — typically index 0 for pointA, 1 for pointB (or
 * vertex / pointA / pointB in order for angular). When
 * `associatedEntities` is shorter than `index + 1` or entry[index]
 * fails to resolve, returns null so the caller can fall back to the
 * coordinate-match path.
 */
function resolveEndpointFromAssocEntities(
  associatedEntities: readonly AssociatedEntityRef[],
  index: number,
  entityById: ReadonlyMap<string, SubselectorResolverEntity>,
): EntityRef | null {
  const entry = associatedEntities[index];
  if (entry === undefined) return null;
  return assocRefToEntityRef(entry, entityById);
}

/**
 * Resolve a 2D point to the (entityId, subselector) pair of the nearest
 * matching entity feature. Returns null when no feature lies within
 * `MATCH_EPS` of the query point.
 *
 * Match priority (first hit wins, inside each loop we try the most specific
 * features first):
 *   1. Line         : start → end
 *   2. Circle / Arc : center
 *   3. LWPOLYLINE   : vertex 0, 1, 2, ...
 *   4. Text / MText
 *      / INSERT / Attdef : insertionPoint   (empty subselector)
 *
 * Iteration order: entities are scanned in input order; the first entity
 * that has a matching feature wins.
 */
export function resolvePointToEntity(
  pt: Vec2,
  entities: readonly (EntityLike & { readonly id: string })[],
): EntityRef | null {
  for (const e of entities) {
    switch (e.type) {
      case "Line": {
        const line = e as unknown as { start: Vec2; end: Vec2 };
        if (near(line.start, pt)) return { id: e.id, subselector: "start" };
        if (near(line.end, pt)) return { id: e.id, subselector: "end" };
        break;
      }
      case "Circle":
      case "Arc": {
        const c = e as unknown as { center: Vec2 };
        if (near(c.center, pt)) return { id: e.id, subselector: "center" };
        break;
      }
      case "Ellipse": {
        const el = e as unknown as { center: Vec2 };
        if (near(el.center, pt)) return { id: e.id, subselector: "center" };
        break;
      }
      case "LWPOLYLINE": {
        const p = e as unknown as { vertices: ReadonlyArray<Vec2> };
        for (let i = 0; i < p.vertices.length; i++) {
          if (near(p.vertices[i]!, pt)) {
            return { id: e.id, subselector: `v${i}` };
          }
        }
        break;
      }
      case "Text":
      case "MText":
      case "INSERT":
      case "Attdef": {
        const t = e as unknown as { insertionPoint: Vec2 };
        if (t.insertionPoint && near(t.insertionPoint, pt)) {
          return { id: e.id, subselector: "" };
        }
        break;
      }
      default:
        // Unknown entity type — skip; the solver would not accept it as a
        // variable source anyway.
        break;
    }
  }
  return null;
}

/**
 * Walk the parameter list and emit one DimensionalConstraint descriptor per
 * non-zero numeric constraint parameter whose reference points all resolve
 * to an entity feature.
 *
 * Rules mirroring DYN-5 gating:
 *   - Parameters with `currentDistance === 0` / `currentAngle === 0` /
 *     `currentRadius === 0` / `currentDiameter === 0` are skipped. A zero
 *     target is ambiguous (it can mean "no constraint set" or "collapsed
 *     geometry") and feeding it to the solver would try to collapse both
 *     endpoints onto the same coordinate — destructive and never desired.
 *   - Non-numeric parameter kinds (linear / flip / visibility / ...) are
 *     ignored; only constraint-* kinds participate here.
 *   - Unresolvable points push a warning and skip that parameter — the
 *     solver never sees an incomplete descriptor.
 *
 * Angles are converted from the author's degrees to solver-canonical
 * radians.
 */
/**
 * Infer an anchor entity id for a purely-dimensional block (no geometric
 * constraints).
 *
 * CONTEXT
 * ───────
 * A block expressed only through dimensional constraints (horizontal /
 * vertical / aligned distance, angular, radial, diameter) is
 * translation-invariant: the LM solver can converge on any rigid
 * translation of the geometry that satisfies every distance. Without
 * pinning at least one entity, the solver's initial-guess bias or
 * warm-start state determines where the block lands — visible as a
 * shifted render relative to the authored basepoint.
 *
 * AutoCAD's constraint engine pins the entity corresponding to the
 * "root" of the constraint graph — the first point that participates
 * as a `pointA` but never as a `pointB`. In GOST profile libraries
 * (ibeam, channel, angle) the authored left edge sits at the origin
 * and all dimensional constraints fan outward from it; the left edge's
 * first vertex is therefore both the topological root and the
 * geometric origin.
 *
 * ALGORITHM
 * ─────────
 *   1. Collect every `pointA` (for horizontal/vertical/aligned:
 *      pointA; for angular: vertex; for radial/diameter: center) and
 *      every `pointB` (pointB for linear distance; pointA + pointB for
 *      angular; none for radial/diameter).
 *   2. A "dangling" pointA is one that does not appear as any pointB
 *      within MATCH_EPS — it is a sink of the constraint DAG, hence a
 *      natural anchor.
 *   3. Resolve each dangling pointA to an entity via
 *      `resolvePointToEntity`. First successful hit wins
 *      (deterministic — entities scanned in input order).
 *   4. Fallback when no dangling pointA resolves: sort all pointAs
 *      lexicographically (x ascending, tie-broken by y ascending) and
 *      take the leftmost-bottommost that resolves. This matches
 *      AutoCAD's "left-edge fixed" convention for GOST profiles.
 *   5. If nothing resolves, return null — the caller keeps the gate
 *      closed and defers to the DYN-5 axial heuristic.
 */
export function inferAnchorEntity(
  parameters: readonly DynamicParameter[],
  entities: readonly (EntityLike & { readonly id: string })[],
): string | null {
  // ── Explicit-ref fast path (AutoCAD 2018+ parametric blocks) ──
  //
  // When at least one constraint parameter carries DXF-sourced
  // `associatedEntities[]` (populated by the
  // `dxfBridge.dynamic.parser.assocdeps` pre-scan), we build the
  // topological graph directly over entity handles instead of matching
  // point coordinates. This bypasses the MATCH_EPS fuzziness and
  // delivers a stable anchor even when the LM solver's initial guess
  // has perturbed the geometry away from its authored positions.
  //
  // Graph semantics (mirrors the coordinate-based algorithm below):
  //   - entityIds from index 0  → "pointA" role (sinks when dangling)
  //   - entityIds from index 1+ → "pointB" role (drives away from anchor)
  //   - radial / diameter contribute only an index-0 entity (the
  //     circle) — it's always a dangling pointA candidate.
  const entityIdSet = new Set(entities.map((e) => e.id));
  const entityAs: string[] = [];
  const entityBs: string[] = [];
  let hasExplicitRefs = false;
  for (const p of parameters) {
    const assoc = getConstraintAssociatedEntities(p);
    if (assoc === null || assoc.length === 0) continue;
    if (isConstraintActive(p)) {
      hasExplicitRefs = true;
      const first = assoc[0];
      if (first && first.entityId.length > 0) entityAs.push(first.entityId);
      for (let i = 1; i < assoc.length; i++) {
        const ref = assoc[i];
        if (ref && ref.entityId.length > 0) entityBs.push(ref.entityId);
      }
    }
  }
  if (hasExplicitRefs && entityAs.length > 0) {
    // Topological root: entity that is pointA but NEVER pointB.
    const danglingIds = entityAs.filter((id) => !entityBs.includes(id));
    for (const id of danglingIds) {
      if (entityIdSet.has(id)) return id;
    }
    // Fallback: first pointA that exists in the entity table. Keeps
    // deterministic ordering with the input.
    for (const id of entityAs) {
      if (entityIdSet.has(id)) return id;
    }
  }

  // ── Coordinate-match fallback (legacy DXF fixtures without assoc refs) ──
  const pointAs: Vec2[] = [];
  const pointBs: Vec2[] = [];

  for (const p of parameters) {
    switch (p.kind) {
      case "constraint-horizontal":
      case "constraint-vertical":
      case "constraint-aligned": {
        const distance = p.currentDistance;
        if (!Number.isFinite(distance) || distance === 0) break;
        pointAs.push(p.pointA);
        pointBs.push(p.pointB);
        break;
      }
      case "constraint-angular": {
        const angle = p.currentAngle;
        if (!Number.isFinite(angle) || angle === 0) break;
        // The vertex is the "hinge" of the angle — it is the fixed
        // reference around which pointA and pointB rotate, so it acts
        // as pointA for anchor-inference purposes.
        pointAs.push(p.vertex);
        pointBs.push(p.pointA);
        pointBs.push(p.pointB);
        break;
      }
      case "constraint-radial": {
        const radius = p.currentRadius;
        if (!Number.isFinite(radius) || radius === 0) break;
        // A radial constraint pins the circle's center relative to its
        // radius — the center is a pointA with no pointB counterpart.
        pointAs.push(p.center);
        break;
      }
      case "constraint-diameter": {
        const diameter = p.currentDiameter;
        if (!Number.isFinite(diameter) || diameter === 0) break;
        pointAs.push(p.center);
        break;
      }
      default:
        break;
    }
  }

  if (pointAs.length === 0) return null;

  // Topological anchor: pointA with no matching pointB.
  const dangling = pointAs.filter(
    (a) => !pointBs.some((b) => near(a, b)),
  );
  for (const pt of dangling) {
    const ref = resolvePointToEntity(pt, entities);
    if (ref) return ref.id;
  }

  // Geometric fallback: leftmost-bottommost resolvable pointA.
  const sorted = [...pointAs].sort(
    (a, b) => a.x - b.x || a.y - b.y,
  );
  for (const pt of sorted) {
    const ref = resolvePointToEntity(pt, entities);
    if (ref) return ref.id;
  }

  return null;
}

/**
 * Read-only access to the `associatedEntities` field on any of the six
 * dimensional constraint parameter kinds. Returns `null` when the
 * parameter is not a constraint kind. Returns an empty array when the
 * parameter IS a constraint but has no DXF-sourced assoc refs
 * (e.g. synthesised fixture or legacy-tier parser path).
 */
function getConstraintAssociatedEntities(
  p: DynamicParameter,
): readonly AssociatedEntityRef[] | null {
  switch (p.kind) {
    case "constraint-horizontal":
    case "constraint-vertical":
    case "constraint-aligned":
    case "constraint-angular":
    case "constraint-radial":
    case "constraint-diameter":
      return p.associatedEntities ?? [];
    default:
      return null;
  }
}

/**
 * Return true when the constraint parameter's current numeric value is
 * non-zero and finite. Mirrors the gating inside
 * `collectDimensionalConstraintsFromParameters`.
 */
function isConstraintActive(p: DynamicParameter): boolean {
  switch (p.kind) {
    case "constraint-horizontal":
    case "constraint-vertical":
    case "constraint-aligned":
      return Number.isFinite(p.currentDistance) && p.currentDistance !== 0;
    case "constraint-angular":
      return Number.isFinite(p.currentAngle) && p.currentAngle !== 0;
    case "constraint-radial":
      return Number.isFinite(p.currentRadius) && p.currentRadius !== 0;
    case "constraint-diameter":
      return Number.isFinite(p.currentDiameter) && p.currentDiameter !== 0;
    default:
      return false;
  }
}

export function collectDimensionalConstraintsFromParameters(
  parameters: readonly DynamicParameter[],
  entities: readonly (EntityLike & { readonly id: string })[],
  warnings: string[],
): DimensionalConstraint[] {
  const out: DimensionalConstraint[] = [];
  const entityIdSet = new Set(entities.map((e) => e.id));
  // Pre-build an id → entity lookup so the subselector resolver can do
  // O(1) lookups per ref, rather than scanning the entity list per ref.
  const entityById = new Map<string, SubselectorResolverEntity>();
  for (const e of entities) {
    entityById.set(
      e.id,
      e as unknown as SubselectorResolverEntity,
    );
  }

  for (const p of parameters) {
    switch (p.kind) {
      case "constraint-horizontal":
      case "constraint-vertical":
      case "constraint-aligned": {
        const distance = p.currentDistance;
        if (!Number.isFinite(distance) || distance === 0) break;

        // Explicit-ref path: when DXF-sourced associatedEntities[] are
        // present and map to entities in the solver's scope, skip the
        // coordinate-match entirely. We need at least two refs (one per
        // endpoint).
        const assocRefs = p.associatedEntities ?? [];
        let refA: EntityRef | null = null;
        let refB: EntityRef | null = null;
        if (assocRefs.length >= 2) {
          const a = resolveEndpointFromAssocEntities(assocRefs, 0, entityById);
          const b = resolveEndpointFromAssocEntities(assocRefs, 1, entityById);
          if (a && b && entityIdSet.has(a.id) && entityIdSet.has(b.id)) {
            refA = a;
            refB = b;
          }
        }
        if (!refA || !refB) {
          refA = resolvePointToEntity(p.pointA, entities);
          refB = resolvePointToEntity(p.pointB, entities);
        }
        if (!refA || !refB) {
          warnings.push(
            `constraint parameter "${p.name}" (${p.kind}): point not resolvable to an entity feature`,
          );
          break;
        }
        out.push({
          id: p.id,
          kind:
            p.kind === "constraint-horizontal"
              ? "horizontal-distance"
              : p.kind === "constraint-vertical"
                ? "vertical-distance"
                : "aligned-distance",
          currentValue: distance,
          entityIds: [refA.id, refB.id],
          entitySubselectors: [refA.subselector, refB.subselector],
        });
        break;
      }
      case "constraint-angular": {
        const angleDeg = p.currentAngle;
        if (!Number.isFinite(angleDeg) || angleDeg === 0) break;

        // Angular wants vertex + pointA + pointB = three refs. Assoc
        // refs (if present) are interpreted positionally:
        //   [0] = vertex, [1] = pointA, [2] = pointB.
        const assocRefs = p.associatedEntities ?? [];
        let refV: EntityRef | null = null;
        let refA: EntityRef | null = null;
        let refB: EntityRef | null = null;
        if (assocRefs.length >= 3) {
          const v = resolveEndpointFromAssocEntities(assocRefs, 0, entityById);
          const a = resolveEndpointFromAssocEntities(assocRefs, 1, entityById);
          const b = resolveEndpointFromAssocEntities(assocRefs, 2, entityById);
          if (
            v && a && b &&
            entityIdSet.has(v.id) &&
            entityIdSet.has(a.id) &&
            entityIdSet.has(b.id)
          ) {
            refV = v;
            refA = a;
            refB = b;
          }
        }
        if (!refV || !refA || !refB) {
          refV = resolvePointToEntity(p.vertex, entities);
          refA = resolvePointToEntity(p.pointA, entities);
          refB = resolvePointToEntity(p.pointB, entities);
        }
        if (!refV || !refA || !refB) {
          warnings.push(
            `constraint parameter "${p.name}" (constraint-angular): vertex/point not resolvable to an entity feature`,
          );
          break;
        }
        out.push({
          id: p.id,
          kind: "angular-distance",
          currentValue: angleDeg * DEG_TO_RAD,
          entityIds: [refV.id, refA.id, refB.id],
          entitySubselectors: [
            refV.subselector,
            refA.subselector,
            refB.subselector,
          ],
        });
        break;
      }
      case "constraint-radial": {
        const radius = p.currentRadius;
        if (!Number.isFinite(radius) || radius === 0) break;

        const assocRefs = p.associatedEntities ?? [];
        let refC: EntityRef | null = null;
        if (assocRefs.length >= 1) {
          const c = resolveEndpointFromAssocEntities(assocRefs, 0, entityById);
          if (c && entityIdSet.has(c.id)) refC = c;
        }
        if (!refC) refC = resolvePointToEntity(p.center, entities);
        if (!refC) {
          warnings.push(
            `constraint parameter "${p.name}" (constraint-radial): center not resolvable to a circle/arc entity`,
          );
          break;
        }
        out.push({
          id: p.id,
          kind: "radial",
          currentValue: radius,
          entityIds: [refC.id],
          entitySubselectors: [refC.subselector],
        });
        break;
      }
      case "constraint-diameter": {
        const diameter = p.currentDiameter;
        if (!Number.isFinite(diameter) || diameter === 0) break;

        const assocRefs = p.associatedEntities ?? [];
        let refC: EntityRef | null = null;
        if (assocRefs.length >= 1) {
          const c = resolveEndpointFromAssocEntities(assocRefs, 0, entityById);
          if (c && entityIdSet.has(c.id)) refC = c;
        }
        if (!refC) refC = resolvePointToEntity(p.center, entities);
        if (!refC) {
          warnings.push(
            `constraint parameter "${p.name}" (constraint-diameter): center not resolvable to a circle/arc entity`,
          );
          break;
        }
        out.push({
          id: p.id,
          kind: "diameter",
          currentValue: diameter,
          entityIds: [refC.id],
          entitySubselectors: [refC.subselector],
        });
        break;
      }
      // Non-constraint parameter kinds (linear / flip / visibility /
      // basepoint / point / polar / xy / rotation / lookup / alignment /
      // user): not part of the dimensional constraint system.
      default:
        break;
    }
  }

  return out;
}
